"use client";

import { useState } from "react";
import { motion } from "motion/react";
import {
  IconGlobe,
  IconGlobeFilled,
  IconSparkles,
  IconWand,
  IconWorld,
} from "@tabler/icons-react";
import {
  Box,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";

const EXAMPLES = [
  "stripe.com",
  "Une agence SEO pour PME",
  "Un cabinet de conseil RH",
];

export function HeroIntro({ onStart }) {
  const [value, setValue] = useState("");

  const submit = () => {
    if (value.trim().length < 2) return;
    onStart(value.trim());
  };

  return (
    <Container size="md" h="calc(80vh - 4rem)">
      <Stack
        align="center"
        justify="center"
        h="100%"
        gap="xl"
        ta="center"
        py="xl"
      >
        {/* Badge d'introduction */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Paper
            radius="xl"
            withBorder
            p="xs"
            px="md"
            bg="rgba(255, 255, 255, 0.7)"
            style={{ backdropFilter: "blur(10px)" }}
          >
            <Group gap="xs">
              <IconSparkles size={14} color="var(--mantine-color-primary-6)" />
              <Text size="xs" fw={500} c="dimmed">
                Magileads · AI Agent Builder
              </Text>
            </Group>
          </Paper>
        </motion.div>

        {/* Titre principal */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <Title
            order={1}
            fw={600}
            fz={64}
            style={{ letterSpacing: "-1px", lineHeight: 1.2 }}
          >
            Créons ensemble votre <br />
            <Text
              span
              variant="gradient"
              gradient={{ from: "violet", to: "blue", deg: 45 }}
              inherit
              fw={600}
            >
              collaborateur commercial IA
            </Text>
          </Title>
        </motion.div>

        {/* Sous-titre */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Text size="xl" maw={600} mx="auto" lh={1.6}>
            Donnez-moi simplement votre site ou décrivez votre activité. Je
            l'analyse, puis nous construisons votre agent de prospection en
            discutant, étape par étape.
          </Text>
        </motion.div>

        {/* Zone de saisie principale */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.16 }}
          style={{ width: "100%" }}
        >
          <Box maw={700} mx="auto" mt="md">
            <TextInput
              autoFocus
              size="xl"
              radius="xl"
              placeholder="votre-site.com ou décrivez votre entreprise…"
              value={value}
              onChange={(e) => setValue(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  !e.nativeEvent.isComposing &&
                  e.keyCode !== 229
                ) {
                  submit();
                }
              }}
              leftSection={
                <IconWorld size={20} color="var(--mantine-color-primary-6)" />
              }
              leftSectionPointerEvents="none"
              rightSectionWidth={140}
              rightSection={
                <Button
                  radius="xl"
                  size="md"
                  variant="gradient"
                  gradient={{ from: "violet", to: "blue", deg: 45 }}
                  onClick={submit}
                  disabled={value.trim().length < 2}
                  leftSection={<IconWand size={16} />}
                  mr={6} // Petite marge pour ne pas coller à la bordure
                >
                  Analyser
                </Button>
              }
              styles={{
                input: {
                  boxShadow: "0 24px 60px -30px rgba(30,41,120,0.25)",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid var(--mantine-color-gray-3)",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  "&:focus": {
                    borderColor: "var(--mantine-color-primary-4)",
                    boxShadow: "0 28px 70px -28px rgba(59,90,220,0.35)",
                  },
                },
              }}
            />

            {/* Exemples */}
            <Group justify="center" gap="sm" mt="xl">
              <Text size="md" c="dimmed">
                Essayez :
              </Text>
              {EXAMPLES.map((ex) => (
                <UnstyledButton
                  key={ex}
                  onClick={() => setValue(ex)}
                  style={(theme) => ({
                    padding: "4px 12px",
                    borderRadius: theme.radius.xl,
                    border: `1px solid ${theme.colors.gray[3]}`,
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    fontSize: theme.fontSizes.md,
                    color: theme.colors.gray[6],
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: theme.colors.primary[4],
                      color: theme.colors.dark[8],
                    },
                  })}
                >
                  {ex}
                </UnstyledButton>
              ))}
            </Group>
          </Box>
        </motion.div>
      </Stack>
    </Container>
  );
}
